import React, { useState } from "react";
import { Card } from "react-bootstrap";
import ReCaptchaV2 from "react-google-recaptcha";
import { config } from "../../config";
import { RegisterUserType } from "../../enums/RegisterUserType";
import "../../styles/registerForm.css";

interface RegisterStepValidationProps {
    firstName: string;
    setFirstName: (val: string) => void;
    lastName: string;
    setLastName: (val: string) => void;
    companyOrTrustName: string;
    setCompanyOrTrustName: (val: string) => void;
    dateOfBirth: string;
    setDateOfBirth: (val: string) => void;
    policyNumber: string;
    setPolicyNumber: (val: string) => void;
    lastPremium: string;
    handleLastPremium: (e: React.ChangeEvent<HTMLInputElement>) => void;
    registrationError: string;
    showLoginButton: boolean;
    isProcessing: boolean;
    requireReCaptchaV2: boolean;
    reCaptchaRefStep1: React.RefObject<any>;
    firstNameRef: React.RefObject<HTMLInputElement>;
    lastNameRef: React.RefObject<HTMLInputElement>;
    companyOrTrustNameRef: React.RefObject<HTMLInputElement>;
    dateOfBirthRef: React.RefObject<HTMLInputElement>;
    policyNumberRef: React.RefObject<HTMLInputElement>;
    onLoginClick: () => void;
    handleCaptchaStep1: (e: React.FormEvent<HTMLInputElement>) => void;
}

const RegisterStepValidation: React.FC<RegisterStepValidationProps> = ({
    firstName,
    setFirstName,
    lastName,
    setLastName,
    companyOrTrustName,
    setCompanyOrTrustName,
    dateOfBirth,
    setDateOfBirth,
    policyNumber,
    setPolicyNumber,
    lastPremium,
    handleLastPremium,
    registrationError,
    showLoginButton,
    isProcessing,
    requireReCaptchaV2,
    reCaptchaRefStep1,
    firstNameRef,
    lastNameRef,
    companyOrTrustNameRef,
    dateOfBirthRef,
    policyNumberRef,
    onLoginClick,
    handleCaptchaStep1
}) => {
    const [ownerType, setOwnerType] = useState<RegisterUserType>(RegisterUserType.Individual);

    const handleToggle = (type: RegisterUserType) => {
        if (type === ownerType) return;
        setOwnerType(type);
        if (type === RegisterUserType.Individual) {
            setCompanyOrTrustName("");
        } else {
            setFirstName("");
            setLastName("");
        }
    };

    return (
        <>
            <main
                id="register-form"
                className="d-flex flex-column min-vh-100 justify-content-center align-items-center"
            >
                <section aria-labelledby="register-form-section">
                    <Card body className="register-card">
                        <form className="add-form">
                            <div>
                                <input id="hdnInputFocus" type="hidden" autoFocus />
                                <h1 className="custom-h1">Registration</h1>
                                {registrationError?.length ? (
                                    <>
                                        {showLoginButton ? (
                                            <>
                                                <label className="register-label register-spacing-bottom" aria-live="assertive" role="alert">
                                                    {registrationError}
                                                </label>
                                                &nbsp;
                                                <a href="#" onClick={onLoginClick}>
                                                    Log-in Here.
                                                </a>
                                            </>
                                        ) : (
                                            <>
                                                <label
                                                    className="text-danger register-label register-spacing-bottom"
                                                    aria-live="assertive"
                                                    role="alert"
                                                >
                                                    {registrationError}
                                                </label>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <label className="register-instruction-text register-spacing-bottom">
                                        Please enter the following to help us verify that it&apos;s you.
                                    </label>
                                )}
                            </div>
                            <div className="register-toggle-container">
                                <button
                                    type="button"
                                    className={`register-toggle-btn ${
                                        ownerType === RegisterUserType.Individual
                                            ? "selected"
                                            : "unselected"
                                    }`}
                                    onClick={() => handleToggle(RegisterUserType.Individual)}
                                >
                                    Individual
                                </button>
                                <button
                                    type="button"
                                    className={`register-toggle-btn ${
                                        ownerType === RegisterUserType.CompanyOrTrust
                                            ? "selected"
                                            : "unselected"
                                    }`}
                                    onClick={() => handleToggle(RegisterUserType.CompanyOrTrust)}
                                >
                                    Company/Trust
                                </button>
                            </div>
                            <fieldset className="register-fieldset">
                                <legend>
                                    Either First and Last Name or Company/Trust Name is required.
                                </legend>
                                <div>
                                    <label className="register-label">
                                        Owner First Name as it appears on your policy
                                    </label>
                                    <input
                                        id="firstName"
                                        type="text"
                                        className="form-control skip-to"
                                        placeholder=""
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        disabled={ownerType === RegisterUserType.CompanyOrTrust}
                                        aria-required={ownerType === RegisterUserType.Individual}
                                        aria-label="Owner First Name"
                                        autoFocus
                                        ref={firstNameRef}
                                    ></input>
                                </div>
                                <div>
                                    <label className="register-label">
                                        Owner Last Name as it appears on your policy
                                    </label>
                                    <input
                                        id="lastName"
                                        type="text"
                                        className="form-control"
                                        placeholder=""
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        disabled={ownerType === RegisterUserType.CompanyOrTrust}
                                        aria-required={ownerType === RegisterUserType.Individual}
                                        aria-label="Owner Last Name"
                                        ref={lastNameRef}
                                    ></input>
                                </div>
                                <div className="register-or-separator">
                                    <span>OR</span>
                                </div>
                                <div>
                                    <label className="register-label">
                                        Owner: Company or Trust Name as it appears on your policy
                                    </label>
                                    <input
                                        id="companyOrTrustName"
                                        type="text"
                                        className="form-control"
                                        placeholder=""
                                        value={companyOrTrustName}
                                        onChange={(e) =>
                                            setCompanyOrTrustName(e.target.value)
                                        }
                                        disabled={ownerType === RegisterUserType.Individual}
                                        minLength={2}
                                        maxLength={81}
                                        aria-required={ownerType === RegisterUserType.CompanyOrTrust}
                                        aria-label="Company or Trust Name"
                                        ref={companyOrTrustNameRef}
                                    />
                                </div>
                            </fieldset>
                            <fieldset className="register-fieldset">
                                <label className="register-label">
                                    Insured Date of Birth <span className="register-required-bold">(Required *)</span>
                                </label>
                                <input
                                    id="dateOfBirth"
                                    type="date"
                                    className="form-control"
                                    placeholder="mm/dd/yyyy"
                                    min="1922-01-01"
                                    max="2022-12-31"
                                    value={dateOfBirth}
                                    onChange={(e) => setDateOfBirth(e.target.value)}
                                    aria-required={true}
                                    aria-label="Insured Date of Birth"
                                    ref={dateOfBirthRef}
                                />
                            </fieldset>
                            <fieldset className="register-fieldset">
                                <legend>
                                    Either Policy Number or Last Payment Amount is required.
                                </legend>
                                <div>
                                    <label className="register-label">Policy Number</label>
                                    <input
                                        id="policyNumber"
                                        type="text"
                                        className="form-control"
                                        placeholder=""
                                        value={policyNumber}
                                        onChange={(e) =>
                                            setPolicyNumber(e.target.value.trim())
                                        }
                                        disabled={lastPremium.length > 0}
                                        aria-label="Policy Number"
                                        ref={policyNumberRef}
                                    />
                                </div>
                                <div className="register-or-separator">
                                    <span>OR</span>
                                </div>
                                <div>
                                    <label className="register-label">Last Payment Amount</label>
                                    <input
                                        id="lastPremium"
                                        type="text"
                                        className="form-control register-last-premium"
                                        placeholder=""
                                        value={lastPremium}
                                        onChange={handleLastPremium}
                                        disabled={policyNumber.length > 0}
                                        aria-label="Last Premium Amount"
                                    />
                                </div>
                            </fieldset>
                            {isProcessing ? (
                                <label className="register-label">Processing...</label>
                            ) : (
                                <div className="register-next-container">
                                    <input
                                        type="button"
                                        value="Next"
                                        className="btnRounded btn-block"
                                        onClick={handleCaptchaStep1}
                                    />
                                    {requireReCaptchaV2 ? (
                                        <ReCaptchaV2
                                            id="recaptcha"
                                            sitekey={config.app.recaptchav2Key}
                                            ref={reCaptchaRefStep1}
                                            aria-required={true}
                                            aria-label="Recaptcha"
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            )}
                        </form>
                    </Card>
                </section>
            </main>
        </>
    );
};

export default RegisterStepValidation;
